/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { AppBar, FormControl, Grid, Tabs, Tab, MenuItem, Dialog, Button, DialogActions } from '@material-ui/core';
import { PartnerPartnership } from 'src/models';
import { useApiError, useAppData, useLoading } from 'src/providers';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { cloneDeep } from 'lodash';
import { useStyles } from '../styles';
import { CreateClientLinkTab } from './CreateClientLinkTab';
import { CreatePartnershipLinkTab } from './CreatePartnershipLinkTab';
import { CreateMLMLinkTab } from './CreateMLMLinkTab';
import { ClientLinkSetup, ClientLink, PartnershipLink, MlmLinkSetup, SubPartnershipLinkSetup } from '../../RegistrationLinksModel';
import { generateClientLink, generatePartnershipLink, getClientLinkSetup, getMLMLinkSetup, getSubPartnershipLinkSetup } from '../../RegistrationLinksService';
import { CustomDialogTitle, FieldLabel, SelectControl, TabPanel } from '../../../../components';
import { a11yProps } from '../../../../utils';
import { trackAmplitudeData } from '../../../../services/amplitudeService';
import { useCommonStyles } from '../../../../styles';

interface PartnershipLinkDialogProps {
  partnerships: PartnerPartnership[];
  partnership?: PartnerPartnership;
  open: boolean;
  handleClose: (clientLink?: ClientLink, partnershipLink?: PartnershipLink, mlmLink?: PartnershipLink) => void;
}

export const PartnershipLinkDialog: FC<PartnershipLinkDialogProps> = memo(({ partnerships, partnership, open, handleClose }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addError } = useApiError();
  const { translations } = useAppData();
  const { setIsLoading } = useLoading();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [clientLinkSetup, setClientLinkSetup] = useState<ClientLinkSetup>();
  const [subPartnershipLinkSetup, setSubPartnershipLinkSetup] = useState<SubPartnershipLinkSetup>();
  const [mlmLinkSetup, setMlmLinkSetup] = useState<MlmLinkSetup>();
  const [selectedPartnership, setSelectedPartnership] = useState<PartnerPartnership>(partnership || partnerships[0]);
  const createClientLinkTabFormRef = useRef<any>();
  const subIBLinksTabFormRef = useRef<any>();
  const mlmLinkTabFormRef = useRef<any>();
  const [translation, setTranslation] = useState<any>([]);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (partnership) {
      setSelectedPartnership(partnership);
    } else {
      setSelectedPartnership(partnerships[0]);
    }
  }, [partnership]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const getData = useCallback(
    async (id: number) => {
      setIsLoading(true);
      try {
        if (selectedPartnership) {
          if (selectedPartnership.partnershipType?.name === 'IB' || selectedPartnership.partnershipType?.name === 'MLM') {
            const response = await getClientLinkSetup(id);
            setClientLinkSetup(response.data);

            if (selectedPartnership.partnershipType?.name === 'MLM' && !selectedPartnership.isLastLevel) {
              const response = await getMLMLinkSetup(id);
              setMlmLinkSetup(response.data);
            }
          } else {
            const response = await getSubPartnershipLinkSetup(id);
            setSubPartnershipLinkSetup(response.data);
          }
        }
      } catch (error) {
        addError(JSON.stringify(error.response));
        if (error.response.data.code === 'MlmLinks.NotAllowed') {
          setIsError(true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [addError, selectedPartnership],
  );

  useEffect(() => {
    if (partnerships && partnerships.length > 0) {
      if (selectedPartnership === null) {
        setSelectedPartnership(partnerships[0]);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership) {
      if (selectedPartnership.partnershipType?.name === 'IB') {
        setActiveTab(0);
      } else if (selectedPartnership.partnershipType?.name === 'MLM') {
        setActiveTab(2);
        if (selectedPartnership.isLastLevel) {
          setActiveTab(0);
        }
      } else {
        setActiveTab(1);
      }
    }
  }, [selectedPartnership]);

  useEffect(() => {
    if (selectedPartnership && !isError) {
      (async () => {
        await getData(selectedPartnership.id);
      })();
    }
  }, [selectedPartnership]);

  const handleTabChange = useCallback((event: any, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handlePartnershipChange = useCallback(
    (event: ChangeEvent<{ value: any }>) => {
      const partnership = partnerships?.find((p) => p.id === Number(event.target.value));

      if (partnership) {
        setSelectedPartnership(partnership);
        setIsError(false);

        setMlmLinkSetup(undefined);
        setClientLinkSetup(undefined);
        setSubPartnershipLinkSetup(undefined);
      }
    },
    [partnerships],
  );

  const handleClientLinkGeneration = useCallback(
    async (values, { setSubmitting }) => {
      const newLink = cloneDeep(values);
      Object.keys(newLink).forEach((key) => {
        if (newLink[key] === '-1') {
          newLink[key] = null;
        }
      });

      let response;
      try {
        setIsLoading(true);
        response = await generateClientLink(newLink, selectedPartnership.id);
        const eventProperties = {
          linkType: 'Client link',
        };
        trackAmplitudeData('partnerPortal_action_regLinkCreation', eventProperties);
      } catch (error) {
        if (error.response.data.errors && error.response.data.errors.LinkName) {
          enqueueSnackbar(error.response.data.errors.LinkName[0].message, {
            variant: 'error',
            preventDuplicate: true,
          });
        } else {
          addError(JSON.stringify(error.response));
        }
      } finally {
        setIsLoading(false);
        setSubmitting(false);
        if (response) {
          handleClose(response.data ? response.data : null);
        }
      }
    },
    [setIsLoading, addError, selectedPartnership, handleClose],
  );

  const handlePartnershipLinkGeneration = useCallback(
    async (values, { setSubmitting }) => {
      Object.keys(values).forEach((key) => {
        if (values[key] === '-1') {
          values[key] = null;
        }
      });

      let response;
      try {
        setIsLoading(true);
        response = await generatePartnershipLink(values, selectedPartnership.id);

        const eventProperties = {
          linkType: 'SubPartnership link',
        };
        trackAmplitudeData('partnerPortal_action_regLinkCreation', eventProperties);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
        setSubmitting(false);
        handleClose(undefined, response.data ? response.data : null);
      }
    },
    [setIsLoading, addError, selectedPartnership, handleClose],
  );

  const handleMlmLinkGeneration = useCallback(
    async (values, { setSubmitting }) => {
      Object.keys(values).forEach((key) => {
        if (values[key] === '-1') {
          values[key] = null;
        }
      });

      let response;
      try {
        setIsLoading(true);
        response = await generatePartnershipLink(values, selectedPartnership.id);

        const eventProperties = {
          linkType: 'MLM link',
        };
        trackAmplitudeData('partnerPortal_action_regLinkCreation', eventProperties);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
        setSubmitting(false);
        handleClose(undefined, undefined, response.data ? response.data : null);
      }
    },
    [setIsLoading, addError, selectedPartnership, handleClose],
  );

  return (
    <Dialog onClose={() => handleClose()} open={open} classes={{ paper: classes.dialog }}>
      <CustomDialogTitle title={translation.text_6235} onClose={() => handleClose()} />
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ marginBottom: 10 }}>
          <FieldLabel label={translation.text_6242} />
          <FormControl variant="outlined" className={commonClasses.formControl}>
            <SelectControl value={selectedPartnership ? selectedPartnership.id : ''} onChange={handlePartnershipChange}>
              {partnerships.map((partnership) => (
                <MenuItem className={commonClasses.optionSelector} key={partnership.partnershipCode} value={partnership.id}>
                  {partnership.partnershipCode}
                </MenuItem>
              ))}
            </SelectControl>
          </FormControl>
        </Grid>
        {selectedPartnership !== null && selectedPartnership?.id && (
          <>
            <AppBar position="static" className={commonClasses.tabs}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                {selectedPartnership.partnershipType && (selectedPartnership.partnershipType.name === 'IB' || selectedPartnership.partnershipType.name === 'MLM') ? (
                  <Tab label={translation.client_link} value={0} {...a11yProps(0)} />
                ) : (
                  <Tab label={translation.sub_partnership_link} value={1} {...a11yProps(1)} />
                )}
                {selectedPartnership.partnershipType?.name === 'MLM' && !selectedPartnership.isLastLevel && <Tab label={translation.mlm_link} value={2} {...a11yProps(2)} />}
              </Tabs>
            </AppBar>
            <TabPanel value={activeTab} index={0}>
              <Grid container direction="row" justify="flex-start" alignItems="center">
                <Formik
                  innerRef={createClientLinkTabFormRef}
                  onSubmit={handleClientLinkGeneration}
                  enableReinitialize
                  initialValues={{
                    linkName: '',
                    countryId: '',
                    platformTradingAccountType: '',
                    language: '',
                    linkType: '',
                    currency: '',
                    shortCode: '',
                    salesCode: '',
                    masterCode: '',
                  }}
                  validationSchema={Yup.object().shape({
                    countryId: Yup.string().required(translation.text_586),
                    platformTradingAccountType: Yup.string().required(translation.text_586),
                    language: Yup.string().required(translation.text_586),
                    linkType: Yup.string().required(translation.text_586),
                    currency: Yup.string().required(translation.text_586),
                    shortCode: Yup.string().matches(/^[a-z0-9_-]{1,50}$/, translation.text_6251),
                  })}
                >
                  {({ setFieldValue, values, touched, errors }) => (
                    <CreateClientLinkTab
                      tradingAccountTypes={clientLinkSetup?.tradingAccountTypes || []}
                      translation={translation}
                      languages={clientLinkSetup?.linkSetupBroker.supportedLanguages ?? []}
                      countries={clientLinkSetup?.countries ?? []}
                      masterCodes={clientLinkSetup?.masterCodeConfigurations ?? []}
                      hasCallCenter={clientLinkSetup?.hasCallCenter || false}
                      values={values}
                      setFieldValue={setFieldValue}
                      touched={touched}
                      errors={errors}
                    />
                  )}
                </Formik>
              </Grid>
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={2} className={classes.container}>
                <Formik
                  innerRef={subIBLinksTabFormRef}
                  onSubmit={handlePartnershipLinkGeneration}
                  enableReinitialize
                  initialValues={{
                    linkName: '',
                    linkConfigurationId: '',
                    shortCode: '',
                  }}
                  validationSchema={Yup.object().shape({
                    linkConfigurationId: Yup.string().required(translation.text_586),
                    shortCode: Yup.string().max(50, 'Too long'),
                  })}
                >
                  {({ touched, errors }) => <CreatePartnershipLinkTab translation={translation} linkConfigurations={subPartnershipLinkSetup?.linkConfigurations ?? []} touched={touched} errors={errors} />}
                </Formik>
              </Grid>
            </TabPanel>
            {mlmLinkSetup && !selectedPartnership.isLastLevel && (
              <TabPanel value={activeTab} index={2}>
                <Grid container spacing={2} className={classes.container}>
                  <Formik
                    innerRef={mlmLinkTabFormRef}
                    onSubmit={handleMlmLinkGeneration}
                    enableReinitialize
                    initialValues={{
                      linkName: '',
                      linkConfigurationId: '',
                      shortCode: '',
                    }}
                    validationSchema={Yup.object().shape({
                      linkConfigurationId: Yup.string().required(translation.text_586),
                      shortCode: Yup.string().max(50, 'Too long'),
                    })}
                  >
                    {({ touched, errors }) => <CreateMLMLinkTab linkConfigurations={mlmLinkSetup?.linkConfigurations ?? []} touched={touched} errors={errors} />}
                  </Formik>
                </Grid>
              </TabPanel>
            )}
          </>
        )}
      </Grid>
      <DialogActions className={classes.dialogAction}>
        <Button
          onClick={() => {
            if (activeTab === 0) {
              createClientLinkTabFormRef?.current?.submitForm();
            }
            if (activeTab === 1) {
              subIBLinksTabFormRef?.current?.submitForm();
            }
            if (activeTab === 2) {
              mlmLinkTabFormRef?.current?.submitForm();
            }
          }}
          className={`${commonClasses.activeDialogBtn} ${classes.btn}`}
        >
          {translation.create_link}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
